import { EDITORS, PEOPLE, taskModerated, type PersonId } from "../data/people";
import type { Busy, ItemStatus, Revision, Status, SubTask, Task, Version } from "../types";
import { formatDuration, plural } from "./utils";
import { at, dayOf } from "./time";

/**
 * Подпись у правки, если её писал не автор задачи: монтажёру важно знать,
 * это постановщик, руководитель или модератор.
 */
export function authorOf(task: Task, item: SubTask, r: Revision): string | undefined {
  if (!r.by || r.by === task.creator) return undefined;
  const name = PEOPLE[r.by].name;
  return item.versions.some((v) => v.mod?.by === r.by) ? `Модератор · ${name}` : name;
}

/** Последняя сданная версия ролика. */
export const lastVersion = (item: SubTask): Version | undefined => item.versions[item.versions.length - 1];

/** Ролики, которые считаются в дне: снятые постановщиком не считаются. */
export const liveItems = (task: Task): SubTask[] => task.items.filter((i) => i.status !== "cancelled");

/**
 * Ролики, которых ждёт модератор отделения.
 *
 * Только `uploaded`: как только он поставил галочку или замечания, ролик с его
 * стола уходит. Достать уже принятое он может, но это его выбор, а не очередь.
 */
export const itemsToModerate = (task: Task): SubTask[] => task.items.filter((i) => i.status === "uploaded" && taskModerated(task, i));

/**
 * Ждёт ли ролик аппрува постановщика: модератор поставил галочку — или у
 * проекта модератора нет вовсе, и ролик принимают прямо со сдачи.
 */
export const awaitsApprove = (task: Task, item: SubTask): boolean =>
  item.status === "moderated" || (item.status === "uploaded" && !taskModerated(task, item));

/** Ролики, ждущие аппрува постановщика. */
export const itemsToApprove = (task: Task): SubTask[] => task.items.filter((i) => awaitsApprove(task, i));

/** Ролики, уехавшие в Telegram и ждущие ссылки на публикацию. */
export const itemsToPublish = (task: Task): SubTask[] => task.items.filter((i) => i.status === "approved");

/** Модератору есть что смотреть в этой задаче. */
export const needsCheck = (task: Task): boolean => itemsToModerate(task).length > 0;

/**
 * Статус постановки по её роликам — то же правило, что на сервере
 * (`taskStatusFrom`). Нужен, чтобы экран показывал переход сразу, не дожидаясь
 * ответа; сервер потом пришлёт задачу целиком и заменит её.
 */
export function statusFromItems(items: SubTask[], task: { acceptedAt?: number; justification?: string; editor?: string }): Status {
  // Исполнителя нет — тема свободная, что бы в ней ни лежало заранее.
  if (task.editor === "") return "open";
  const live = items.filter((i) => i.status !== "cancelled");
  if (live.length === 0) return items.length > 0 ? "done" : task.acceptedAt ? "work" : "wait";
  if (live.every((i) => i.status === "published")) return "done";
  // Обоснование закрывает день, только пока в нём ничего не происходит: возврат
  // ролика модератором открывает день заново, даже если он уже был закрыт.
  const busy: ItemStatus[] = ["uploaded", "moderated", "approved", "rework"];
  if (task.justification && !live.some((i) => busy.includes(i.status))) return "done";
  if (live.some((i) => i.status === "rework")) return "rework";
  if (live.some((i) => i.status === "uploaded" || i.status === "moderated")) return "review";
  if (live.some((i) => i.status === "approved")) return "publish";
  return task.acceptedAt ? "work" : "wait";
}

/** Рабочий день монтажёра, часы */
export const CAPACITY = 8;

/** Ролики задачи. Оставлено функцией: так читается «пункты этой задачи», а не «поле». */
export const taskItems = (t: Task): SubTask[] => t.items;

/** Сколько задач у монтажёра в этот день: ролики — по отдельности, работа вне воронки — тоже задача, выполненные не считаем */
export function dayTaskCount(editor: PersonId, day: number, tasks: Task[], busy: Busy[] = [], excludeTaskId?: string) {
  const own = tasks.reduce(
    (n, t) => (t.editor === editor && t.id !== excludeTaskId && t.status !== "done" && (t.proposal ? t.proposal.day : t.day) === day ? n + liveItems(t).length : n),
    0,
  );
  return own + busy.filter((b) => b.editor === editor && b.day === day && b.kind === "busy").length;
}

export const tasksLabel = (n: number) => (n === 0 ? "нет задач" : `${n} ${plural(n, ["ролик", "ролика", "роликов"])}`);

/** Часы монтажёра в конкретный день: задачи воронки + эфиры и планёрки */
export function dayLoad(editor: PersonId, day: number, tasks: Task[], busy: Busy[], excludeTaskId?: string) {
  let h = 0;
  for (const t of tasks) {
    if (t.editor !== editor || t.id === excludeTaskId) continue;
    const d = t.proposal ? t.proposal.day : t.day;
    if (d === day) h += t.estimate;
  }
  busy.forEach((b) => {
    if (b.editor === editor && b.day === day) h += b.hours;
  });
  return h;
}

/** Что у монтажёра в этот день */
export function dayItems(editor: PersonId, day: number, tasks: Task[], busy: Busy[]) {
  return {
    tasks: tasks.filter((t) => t.editor === editor && t.day === day),
    proposals: tasks.filter((t) => t.editor === editor && t.proposal?.day === day),
    busy: busy.filter((b) => b.editor === editor && b.day === day),
  };
}

/** Кто свободнее всех в этот день — у кого меньше задач */
export function freestOn(day: number, tasks: Task[], busy: Busy[], editors: PersonId[] = EDITORS) {
  return [...editors].sort((a, b) => dayTaskCount(a, day, tasks, busy) - dayTaskCount(b, day, tasks, busy) || dayLoad(a, day, tasks, busy) - dayLoad(b, day, tasks, busy))[0];
}

export const isPastDay = (day: number, now: number) => day < dayOf(now);

/** Сроки «день в день»: в тот же день до … */
export function dueOptions(day: number, now: number) {
  return [12, 14, 16, 18, 20]
    .map((h) => ({ value: at(day, h), label: `до ${h}:00` }))
    .filter((o) => o.value > now + 30);
}

export function defaultDue(day: number, now: number) {
  const opts = dueOptions(day, now);
  return (opts.find((o) => o.value === at(day, 18)) ?? opts[opts.length - 1] ?? { value: at(day, 20) }).value;
}

/** Пост картинками: правка привязана не к секунде, а к номеру слайда (08.10.2026). */
export const isPostItem = (item: Pick<SubTask, "format"> & { versions?: Version[] }) =>
  /пост|карусел/i.test(item.format) || Boolean(item.versions?.some((v) => v.images?.length));

/** Метка правки: «0:12» у ролика, «слайд 2» у поста (в tc — номер слайда с 1). */
export const revisionAt = (tc: number, post: boolean) => (post ? `слайд ${Math.max(1, tc)}` : formatDuration(tc));

/** Что делаем (08.10.2026): ролик или пост картинками — от этого зависит, что сдаёт монтажёр. */
export const PUB_FORMATS = [
  // Без названий соцсетей (09.10.2026): «Рилс» на YouTube-канале путал. Куда
  // выходит — говорит аккаунт, формат — только про сам ролик.
  { value: "Видео 9:16", label: "Видео 9:16" },
  { value: "Видео 16:9", label: "Видео 16:9" },
  { value: "Пост 4:5", label: "Пост / карусель" },
  { value: "Сторис", label: "Сторис" },
] as const;
